// src/components/SuperAdminStepUpModal.jsxsrc/components/SuperAdminStepUpModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, AlertCircle, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Input, Label } from '@/components/ui/input.jsx';
import { apiCall } from '../utils/api';

export default function SuperAdminStepUpModal({ onSuccess, onCancel }) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Auto-focus password field on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await apiCall('/super-admin/step-up', {
        method: 'POST',
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        const body = await res.json();
        onSuccess(body.expires_at, body.session_minutes);
      } else {
        const errorBody = await res.json().catch(() => ({}));
        // Rate-limit case
        if (res.status === 429) {
          setError(errorBody.detail || 'Too many failed attempts. Please wait before trying again.');
        } else if (res.status === 401) {
          setError('Incorrect password.');
        } else if (res.status === 404) {
          // Shouldn't happen if the modal is showing, but defensive
          setError('You do not have super-admin access.');
        } else {
          setError('Step-up failed. Please try again.');
        }
        setPassword('');
      }
    } catch (err) {
      console.error('Step-up error:', err);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      // Block click-outside — this is a security dialog, not a casual modal
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-full max-w-md bg-[#0f0f0f] border border-[#2a2a2a] rounded-2xl p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Super-Admin Verification</h3>
              <p className="text-sm text-[#a0a0a0] mt-0.5">
                Confirm your password to access super-admin tools.
              </p>
            </div>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-[#a0a0a0] hover:text-white flex-shrink-0"
              disabled={submitting}
              aria-label="Cancel"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="mb-4 p-3 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
          <p className="text-xs text-[#a0a0a0] leading-relaxed">
            Your session will be valid for 5 minutes. All super-admin actions
            are logged with your email, IP, and timestamp.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Password</Label>
            <Input
              ref={inputRef}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Your current password"
              required
              autoComplete="current-password"
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            {onCancel && (
              <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={submitting || !password}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  Verify
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}